import {
    ACTIVITY_ATTRIBUTE_KEY,
    ACTIVITY_AUTOMATION_SOURCE_PATTERN,
    ACTIVITY_EVENT_CATEGORY,
    ACTIVITY_EVENT_CATEGORIES,
    ACTIVITY_EVENT_TYPE,
    ACTIVITY_FACE_MATCH_STATUS,
    ACTIVITY_INPUT_SOURCE,
    ACTIVITY_MAX_AUTOMATION_SOURCE_LENGTH,
    ACTIVITY_MAX_FIELD_TYPE_LENGTH,
    ACTIVITY_MAX_SCHEMA_KEY_LENGTH,
    ACTIVITY_MAX_YMS_EXPORT_ATTEMPT_NUMBER,
    ACTIVITY_SCHEMA_KEY_PATTERN,
    ACTIVITY_SCHEMA_VERSION,
    ACTIVITY_TARGET_TYPE,
    ACTIVITY_WORK_OBJECT_TYPE,
    ACTIVITY_YMS_EXPORT_RESULT,
} from 'utils/activityTracking/constants';

/**
 * The one place an activity event envelope is built (BEAST-13036).
 *
 * Two invariants live here and nowhere else, both of which fail silently if broken:
 *
 * 1. ALL 19 CLIENT-OWNED KEYS ARE EMITTED ON EVERY EVENT, with an explicit `null` where there is no
 *    value. The shared schema declares all 22 of its fields `required`. The AIQ backend does absorb
 *    an omission for us (an absent key binds to `null` and its outbound envelope is
 *    `@JsonInclude(ALWAYS)`), but treat that as their belt and this as our braces: a change to
 *    someone else's mapper configuration must never become our data loss. So the object below is
 *    built as one flat literal - never by conditionally spreading keys in - and nothing downstream
 *    may strip nulls on the way out.
 *
 * 2. `undefined` IS NOT `null`. `JSON.stringify` silently drops any key whose value is `undefined`,
 *    so an `end_reason: maybeReason()` that returns `undefined` produces an envelope with no
 *    `end_reason` at all. Every optional value is therefore coalesced to `null` explicitly here, and
 *    `undefined` must never reach the payload.
 *
 * The three identity fields (`actor_id`, `identity_namespace`, `actor_type`) are stamped by the AIQ
 * backend and the two provenance fields (`source_system`, `ingested_at`) by the shared service. This
 * factory takes no parameter that could set any of them, which is what makes it impossible for a
 * caller to forge attribution.
 */

/** Coalesces to a capped string or `null`. The envelope's string ids are all capped at 256. */
function toNullableString(value, maxLength = 256) {
    if (value === null || value === undefined) return null;
    const asString = String(value);
    if (!asString) return null;
    return asString.length > maxLength ? asString.slice(0, maxLength) : asString;
}

/**
 * Coalesces an operational id to a JSON number or `null`.
 *
 * The types here are deliberately asymmetric and easy to get backwards: `work_object_id` and
 * `target_id` are strings, so a numeric transaction id has to be stringified, while
 * `location_id` / `gate_id` / `lane_id` / `desk_id` are numbers, so they must NOT be. The shared
 * schema wants those four as strings too, but the AIQ backend binds them as `Long` and converts on
 * its outbound leg - pre-stringifying them here breaks its deserialization.
 */
function toNullableNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    const asNumber = Number(value);
    return Number.isFinite(asNumber) ? asNumber : null;
}

/** An optional attribute that would violate the shared schema is omitted, never sent invalid. */
function isValidAttributeValue(value, maxLength) {
    return typeof value === 'string' && value.length > 0 && value.length <= maxLength && ACTIVITY_SCHEMA_KEY_PATTERN.test(value);
}

/** The `result` values the backend accepts on `yms_export.responded`. */
const YMS_EXPORT_RESULTS = new Set(Object.values(ACTIVITY_YMS_EXPORT_RESULT));

/**
 * `attempt_number` as the backend wants it: an integer 1..99999 sent as a decimal string, or `null`.
 * A dropped value only shows up later as a backend rejection, so development builds warn here.
 */
function toAttemptNumberString(value) {
    if (!Number.isInteger(value) || value < 1 || value > ACTIVITY_MAX_YMS_EXPORT_ATTEMPT_NUMBER) {
        if (process.env.NODE_ENV === 'development' && value !== null && value !== undefined) {
            console.warn('activityTracking: invalid attempt_number dropped from the envelope.', value);
        }
        return null;
    }
    return String(value);
}

/**
 * Builds `attributes` from one per-event-type mapping, so a key can never reach the wrong event
 * type by accident: `input_source` on a `field.focus.*` event and `page_key` on a field event are
 * both dropped by the backend's own whitelist, which is a bug you cannot see in the response.
 *
 * Always an object, `{}` at minimum, because `attributes` is itself one of the required keys.
 * Values are always strings - the shared schema wants strings, and a boolean `true` that survived
 * our side would become the string `"true"` on theirs, which is a worse fact than one we chose.
 */
function buildAttributes(
    eventType,
    {
        pageKey,
        visibilityState,
        fieldKey,
        fieldType,
        inputSource,
        automationSource,
        reviewResult,
        state,
        previousState,
        stateReason,
        snapshotReason,
        attemptNumber,
        ymsResult,
        faceMatch,
    }
) {
    const attributes = {};

    if (isFaceMatchEventType(eventType)) {
        return buildFaceMatchAttributes(eventType, faceMatch ?? {});
    }

    if (eventType === ACTIVITY_EVENT_TYPE.WORK_OBJECT_FOCUS_STARTED || eventType === ACTIVITY_EVENT_TYPE.WORK_OBJECT_FOCUS_ENDED) {
        if (isValidAttributeValue(pageKey, ACTIVITY_MAX_SCHEMA_KEY_LENGTH)) {
            attributes[ACTIVITY_ATTRIBUTE_KEY.PAGE_KEY] = pageKey;
        }
        if (visibilityState) {
            attributes[ACTIVITY_ATTRIBUTE_KEY.VISIBILITY_STATE] = visibilityState;
        }
        return attributes;
    }

    if (
        eventType === ACTIVITY_EVENT_TYPE.FIELD_FOCUS_STARTED ||
        eventType === ACTIVITY_EVENT_TYPE.FIELD_FOCUS_ENDED ||
        eventType === ACTIVITY_EVENT_TYPE.FIELD_VALUE_APPLIED ||
        eventType === ACTIVITY_EVENT_TYPE.FIELD_REVIEW_COMPLETED
    ) {
        // `field_key` is required on all three, and carries the SAME transformed value as
        // `target_id` - never the raw key in one and the guarded key in the other, which would key
        // the started and ended events of one period two different ways.
        if (isValidAttributeValue(fieldKey, ACTIVITY_MAX_SCHEMA_KEY_LENGTH)) {
            attributes[ACTIVITY_ATTRIBUTE_KEY.FIELD_KEY] = fieldKey;
        }
        if (isValidAttributeValue(fieldType, ACTIVITY_MAX_FIELD_TYPE_LENGTH)) {
            attributes[ACTIVITY_ATTRIBUTE_KEY.FIELD_TYPE] = fieldType;
        }

        if (eventType === ACTIVITY_EVENT_TYPE.FIELD_VALUE_APPLIED) {
            if (inputSource) {
                attributes[ACTIVITY_ATTRIBUTE_KEY.INPUT_SOURCE] = inputSource;
            }
            // Sent ONLY alongside `input_source = automation`. The backend rejects the event when
            // this rides with `manual`, so the two conditions are enforced here rather than trusted
            // from the caller: the right shape, and the right companion.
            const isAutomation = inputSource === ACTIVITY_INPUT_SOURCE.AUTOMATION;
            const isSendableSource =
                typeof automationSource === 'string' &&
                automationSource.length > 0 &&
                automationSource.length <= ACTIVITY_MAX_AUTOMATION_SOURCE_LENGTH &&
                ACTIVITY_AUTOMATION_SOURCE_PATTERN.test(automationSource);

            if (isAutomation && isSendableSource) {
                attributes[ACTIVITY_ATTRIBUTE_KEY.AUTOMATION_SOURCE] = automationSource;
            }
        }

        if (eventType === ACTIVITY_EVENT_TYPE.FIELD_REVIEW_COMPLETED && reviewResult) {
            attributes[ACTIVITY_ATTRIBUTE_KEY.REVIEW_RESULT] = reviewResult;
        }

        return attributes;
    }

    if (eventType === ACTIVITY_EVENT_TYPE.OPERATOR_STATE_CHANGED) {
        if (state) attributes[ACTIVITY_ATTRIBUTE_KEY.STATE] = state;
        if (previousState) attributes[ACTIVITY_ATTRIBUTE_KEY.PREVIOUS_STATE] = previousState;
        if (stateReason) attributes[ACTIVITY_ATTRIBUTE_KEY.STATE_REASON] = stateReason;
        return attributes;
    }

    if (eventType === ACTIVITY_EVENT_TYPE.OPERATOR_HEARTBEAT) {
        if (state) attributes[ACTIVITY_ATTRIBUTE_KEY.STATE] = state;
        return attributes;
    }

    if (eventType === ACTIVITY_EVENT_TYPE.OPERATOR_STATE_SNAPSHOT) {
        if (state) attributes[ACTIVITY_ATTRIBUTE_KEY.STATE] = state;
        if (snapshotReason) attributes[ACTIVITY_ATTRIBUTE_KEY.SNAPSHOT_REASON] = snapshotReason;
        return attributes;
    }

    if (eventType === ACTIVITY_EVENT_TYPE.YMS_EXPORT_REQUESTED || eventType === ACTIVITY_EVENT_TYPE.YMS_EXPORT_RESPONDED) {
        // Both are required by the backend, so an invalid value is omitted and the event is rejected
        // loudly in `rejected[]`, rather than sent with a value we made up.
        const attempt = toAttemptNumberString(attemptNumber);
        if (attempt) attributes[ACTIVITY_ATTRIBUTE_KEY.ATTEMPT_NUMBER] = attempt;

        // `result` exists on the responded event only; the backend rejects it on requested.
        if (eventType === ACTIVITY_EVENT_TYPE.YMS_EXPORT_RESPONDED && YMS_EXPORT_RESULTS.has(ymsResult)) {
            attributes[ACTIVITY_ATTRIBUTE_KEY.RESULT] = ymsResult;
        }
    }

    return attributes;
}

/**
 * `face_match.*` (BEAST-13916). The backend rejects a success carrying an error field and an error carrying a
 * score, so each status writes only its own keys, whatever the caller passed.
 */
function buildFaceMatchAttributes(
    eventType,
    { attemptNumber, cameraPair, inputSource, trigger, status, confidenceScore, confidenceBand, errorType, errorMessage }
) {
    const attributes = {};
    if (Number.isInteger(attemptNumber) && attemptNumber > 0) attributes[ACTIVITY_ATTRIBUTE_KEY.ATTEMPT_NUMBER] = String(attemptNumber);
    if (cameraPair) attributes[ACTIVITY_ATTRIBUTE_KEY.CAMERA_PAIR] = cameraPair;

    if (eventType === ACTIVITY_EVENT_TYPE.FACE_MATCH_REQUESTED) {
        if (inputSource) attributes[ACTIVITY_ATTRIBUTE_KEY.INPUT_SOURCE] = inputSource;
        if (trigger) attributes[ACTIVITY_ATTRIBUTE_KEY.TRIGGER] = trigger;
        return attributes;
    }

    if (status) attributes[ACTIVITY_ATTRIBUTE_KEY.STATUS] = status;
    if (status === ACTIVITY_FACE_MATCH_STATUS.SUCCESS) {
        if (confidenceScore) attributes[ACTIVITY_ATTRIBUTE_KEY.CONFIDENCE_SCORE] = confidenceScore;
        if (confidenceBand) attributes[ACTIVITY_ATTRIBUTE_KEY.CONFIDENCE_BAND] = confidenceBand;
    } else if (status === ACTIVITY_FACE_MATCH_STATUS.ERROR) {
        if (errorType) attributes[ACTIVITY_ATTRIBUTE_KEY.ERROR_TYPE] = errorType;
        if (errorMessage) attributes[ACTIVITY_ATTRIBUTE_KEY.ERROR_MESSAGE] = errorMessage;
    }
    return attributes;
}

/**
 * @param {object} intent the event's own facts, all stamped at the moment the interaction happened
 * @returns {object} the 19-key envelope, ready to serialize
 */
export function buildActivityEvent({
    eventId,
    eventType,
    occurredAt,
    producerSessionId,
    producerSequence,
    workObjectId,
    targetId,
    correlationId = null,
    endReason = null,
    pageKey = null,
    visibilityState = null,
    fieldKey = null,
    fieldType = null,
    inputSource = null,
    automationSource = null,
    reviewResult = null,
    state = null,
    previousState = null,
    stateReason = null,
    snapshotReason = null,
    attemptNumber = null,
    ymsResult = null,
    faceMatch = null,
    locationId = null,
    gateId = null,
    deskId = null,
}) {
    const gate = toNullableNumber(gateId);

    return {
        event_id: toNullableString(eventId, 256),
        event_type: eventType ?? null,
        event_category: resolveEventCategory(eventType),
        schema_version: ACTIVITY_SCHEMA_VERSION,
        occurred_at: occurredAt ?? null,
        producer_session_id: toNullableString(producerSessionId, 256),
        producer_sequence: Number.isFinite(producerSequence) ? producerSequence : null,
        work_object_type: isOperatorEventType(eventType) ? null : ACTIVITY_WORK_OBJECT_TYPE,
        work_object_id: isOperatorEventType(eventType) ? null : toNullableString(workObjectId, 256),
        target_type: resolveTargetType(eventType),
        target_id: isOperatorEventType(eventType) ? null : toNullableString(targetId, 256),
        correlation_id: toNullableString(correlationId, 256),
        // Unused in phase one, so always an explicit null. The shared schema types it `uuid | null`,
        // so an empty string would fail it.
        causation_id: null,
        // Required if and only if the event type ends in `.ended`; the backend enforces both halves
        // with a cross-field check, so setting it on a `.started` event gets that event rejected.
        end_reason: isEndedEventType(eventType) ? (endReason ?? null) : null,
        attributes: buildAttributes(eventType, {
            pageKey,
            visibilityState,
            fieldKey,
            fieldType,
            inputSource,
            automationSource,
            reviewResult,
            state,
            previousState,
            stateReason,
            snapshotReason,
            attemptNumber,
            ymsResult,
            faceMatch,
        }),
        location_id: toNullableNumber(locationId),
        gate_id: gate,
        // In AIQ a lane IS a gate: there is no separate lane id anywhere in the client, and sticky
        // lanes are keyed by `gateId`. Both columns carrying the same number is the domain, not a
        // copy-paste bug. Agreed on both sides and recorded in the backend plan.
        lane_id: gate,
        desk_id: toNullableNumber(deskId),
    };
}

/** Only `event_type` discriminates the three field events; `target_type` rides on top of it. */
export function resolveTargetType(eventType) {
    switch (eventType) {
        case ACTIVITY_EVENT_TYPE.WORK_OBJECT_FOCUS_STARTED:
        case ACTIVITY_EVENT_TYPE.WORK_OBJECT_FOCUS_ENDED:
            return ACTIVITY_TARGET_TYPE.TRANSACTION_TAB;
        case ACTIVITY_EVENT_TYPE.FIELD_FOCUS_STARTED:
        case ACTIVITY_EVENT_TYPE.FIELD_FOCUS_ENDED:
        case ACTIVITY_EVENT_TYPE.FIELD_VALUE_APPLIED:
        case ACTIVITY_EVENT_TYPE.FIELD_REVIEW_COMPLETED:
            return ACTIVITY_TARGET_TYPE.TRANSACTION_FIELD;
        case ACTIVITY_EVENT_TYPE.YMS_EXPORT_REQUESTED:
        case ACTIVITY_EVENT_TYPE.YMS_EXPORT_RESPONDED:
            return ACTIVITY_TARGET_TYPE.YMS_EXPORT;
        case ACTIVITY_EVENT_TYPE.FACE_MATCH_REQUESTED:
        case ACTIVITY_EVENT_TYPE.FACE_MATCH_RESPONDED:
            return ACTIVITY_TARGET_TYPE.FACE_MATCH;
        default:
            return null;
    }
}

export function isFaceMatchEventType(eventType) {
    return eventType === ACTIVITY_EVENT_TYPE.FACE_MATCH_REQUESTED || eventType === ACTIVITY_EVENT_TYPE.FACE_MATCH_RESPONDED;
}

export function isOperatorEventType(eventType) {
    return [ACTIVITY_EVENT_TYPE.OPERATOR_STATE_CHANGED, ACTIVITY_EVENT_TYPE.OPERATOR_HEARTBEAT, ACTIVITY_EVENT_TYPE.OPERATOR_STATE_SNAPSHOT].includes(
        eventType
    );
}

export function resolveEventCategory(eventType) {
    return isOperatorEventType(eventType) ? ACTIVITY_EVENT_CATEGORIES.OPERATOR_STATE : ACTIVITY_EVENT_CATEGORY;
}

/** `field.value.applied` is not an `…ended` type, so a non-null `end_reason` would reject it. */
export function isEndedEventType(eventType) {
    return eventType === ACTIVITY_EVENT_TYPE.WORK_OBJECT_FOCUS_ENDED || eventType === ACTIVITY_EVENT_TYPE.FIELD_FOCUS_ENDED;
}
